/**
 * Informations légales et administratives de Solution Éco France.
 *
 * Source unique pour les mentions légales, CGV, factures, signatures email,
 * en-têtes de PDF, et toute communication corporate.
 *
 * Champs à `null` = pas encore renseigné en attendant la production.
 * Remplacer chaque `null` par la valeur réelle avant la bascule DNS prod.
 */
export interface LegalEntity {
  /** Raison sociale officielle. */
  legalName: string;
  /** Forme juridique. */
  legalForm: 'SAS';
  /** Capital social en EUR (entier). `null` si non encore renseigné. */
  capitalEur: number | null;
  /** SIREN (9 chiffres, sans espace). */
  siren: string;
  /** SIRET du siège (14 chiffres, sans espace). `null` si non confirmé. */
  siretSiege: string | null;
  /** Ville du RCS (ex. "Paris", "Lyon"). `null` si non confirmé. */
  rcsCity: string | null;
  /** Code APE / NAF (5 caractères, ex. "7022Z"). `null` si non confirmé. */
  apeCode: string | null;
  /** Numéro TVA intracommunautaire (sans espace, ex. "FR12345678901"). `null` si non actif. */
  vatNumber: string | null;
  /** Adresse postale du siège social. */
  headquarters: {
    street: string | null;
    postalCode: string | null;
    city: string | null;
    country: string;
  };
  /** Téléphone principal au format international (ex. "+33 1 23 45 67 89"). */
  phone: string | null;
  /** Email de contact principal. */
  email: string;
  /** Email pour les signalements LCEN. */
  abuseEmail: string;
  /** Représentant légal — directeur de la publication (LCEN art. 6). */
  publicationDirector: string | null;
  /** Référence ORIAS (courtage énergie) — facultatif tant que pôle non lancé. */
  oriasNumber: string | null;
  /** Médiateur de la consommation pour les pôles Travaux et CEE. */
  consumerMediator: string | null;
}

export const LEGAL: LegalEntity = {
  legalName: 'SOLUTION ÉCO FRANCE',
  legalForm: 'SAS',
  capitalEur: 1000,
  siren: '982431033',
  siretSiege: '98243103300017',
  rcsCity: 'Nanterre',
  apeCode: '46.90Z',
  vatNumber: 'FR06982431033',
  headquarters: {
    street: '14 rue Beffroy',
    postalCode: '92200',
    city: 'Neuilly-sur-Seine',
    country: 'France',
  },
  // Téléphone non affiché publiquement — contact via email uniquement.
  // Les composants front et templates email gèrent déjà le `null` (omission conditionnelle).
  phone: null,
  email: 'contact@solutionecofrance.fr',
  abuseEmail: 'abuse@solutionecofrance.fr',
  publicationDirector: 'Mohamed NHARI',
  // Non requis : la société n'opère pas de courtage énergie (pas d'inscription ORIAS nécessaire).
  oriasNumber: null,
  consumerMediator:
    'AME CONSO — 197 Boulevard Saint-Germain, 75007 Paris — www.mediationconso-ame.com',
};

/** Helper d'affichage : valeur réelle ou placeholder visible « à compléter ». */
export function legalValue(value: string | number | null, hint = ''): string {
  if (value === null || value === undefined || value === '') {
    return hint ? `[à compléter — ${hint}]` : '[à compléter avant production]';
  }
  return String(value);
}

/** Format SIREN avec espaces (xxx xxx xxx). */
export function formatSiren(siren: string): string {
  return siren.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');
}

/** Format SIRET avec espaces (xxx xxx xxx xxxxx). */
export function formatSiret(siret: string | null): string {
  if (!siret) return legalValue(null, 'SIRET siège');
  return siret.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4');
}

/** Adresse formatée multi-ligne (HTML breaks ou string). */
export function formatHeadquarters(): string {
  const h = LEGAL.headquarters;
  if (!h.street || !h.postalCode || !h.city) {
    return legalValue(null, 'adresse complète du siège');
  }
  return `${h.street}, ${h.postalCode} ${h.city}, ${h.country}`;
}
